/**
 * useEmotionSequence Hook
 * 
 * React hook for managing emotions in the avatar system.
 * 
 * Handles the application of a single emotion that persists until
 * the next user interaction triggers a new emotion analysis.
 */

import { useState, useCallback } from 'react';
import { EmotionType, DEFAULT_EMOTION } from '../config/emotions';
import { EmotionSequence } from '../context/SessionServicesContext';
import { logger } from '../utils/logger';

/**
 * Return type for useEmotionSequence hook
 */
export interface UseEmotionSequenceReturn {
  /** Currently loaded emotion */
  currentSequence: EmotionSequence | null;
  
  /** Currently displayed emotion */
  currentEmotion: EmotionType;
  
  /** Emotion history (last 5 emotions) */
  emotionHistory: EmotionType[];
  
  /** Set a new emotion (applies immediately) */
  setSequence: (sequence: EmotionSequence) => void;
  
  /** Apply the current emotion (for backward compatibility) */
  applyEmotion: () => void;
  
  /** Reset to initial state */
  reset: () => void;
}

/**
 * Hook for managing emotions
 * 
 * @param initialEmotion - Initial emotion to display (default: 'neutral')
 * @returns Emotion management functions and state
 * 
 * @example
 * const emotionSequence = useEmotionSequence('neutral');
 * 
 * // When user finishes speaking and we get emotion from API
 * emotionSequence.setSequence({ emotion: "empathetic", confidence: 0.92, ... });
 * // Emotion is applied immediately and stays until next interaction
 */
export function useEmotionSequence(
  initialEmotion: EmotionType = DEFAULT_EMOTION
): UseEmotionSequenceReturn {
  // Current emotion from API
  const [currentSequence, setCurrentSequence] = useState<EmotionSequence | null>(null);
  
  // Currently displayed emotion
  const [currentEmotion, setCurrentEmotion] = useState<EmotionType>(initialEmotion);
  
  // Emotion history (last 5 emotions for context)
  const [emotionHistory, setEmotionHistory] = useState<EmotionType[]>([initialEmotion]);

  /**
   * Set a new emotion
   * Applies the emotion immediately and it persists until next interaction
   */
  const setSequence = useCallback((sequence: EmotionSequence) => {
    logger.debug('[useEmotionSequence] New emotion received:', {
      emotion: sequence.emotion,
      confidence: sequence.confidence,
      hasReasoning: Boolean(sequence.reasoning)
    });
    
    setCurrentSequence(sequence);
    // Apply emotion immediately
    setCurrentEmotion(sequence.emotion);
    
    // Update emotion history (keep last 5)
    setEmotionHistory(prev => {
      const newHistory = [...prev, sequence.emotion];
      return newHistory.slice(-5); // Keep only last 5
    });
    
    logger.debug('[useEmotionSequence] 🎭 Applied emotion:', sequence.emotion);
  }, []);

  /**
   * Apply the current emotion
   * For backward compatibility - emotion is already applied in setSequence
   */
  const applyEmotion = useCallback(() => {
    if (!currentSequence) {
      console.warn('[useEmotionSequence] No emotion set');
      return;
    }
    
    // Already applied in setSequence, but keeping this for compatibility
    logger.debug('[useEmotionSequence] 🎭 Emotion already applied:', currentSequence.emotion);
  }, [currentSequence]);

  /**
   * Reset to initial state
   * Clears emotion and returns to initial emotion
   */
  const reset = useCallback(() => {
    logger.debug('[useEmotionSequence] Resetting to initial state');
    
    setCurrentSequence(null);
    setCurrentEmotion(initialEmotion);
    setEmotionHistory([initialEmotion]);
  }, [initialEmotion]);

  return {
    currentSequence,
    currentEmotion,
    emotionHistory,
    setSequence,
    applyEmotion,
    reset
  };
}
